import HttpRequest from '@/util/HttpRequest'
import constant from '../../constant/constant'
import VueCookie from 'vue-cookie'

// 캐시 관련 유틸리티 추가
const promiseCache = new Map();

function getCacheKey(actionName, params) {
  return `${actionName}:${JSON.stringify(params)}`;
}

/**
 * API 호출 결과를 캐싱하는 함수
 * @param {string} cacheKey - 캐시 키
 * @param {Function} apiCallFn - API 호출 함수
 * @param {number} memCacheTime - 메모리 캐싱 시간 (밀리초), 기본값 5분
 * @param {boolean} useLocalStorage - 로컬 스토리지 사용 여부, 기본값 true
 * @param {number} localStorageTime - 로컬 스토리지 캐싱 시간 (밀리초), 기본값 5분
 * @returns {Promise} API 호출 결과를 담은 Promise
 */
function cachedApiCall(cacheKey, apiCallFn, memCacheTime = 300000, useLocalStorage = true, localStorageTime = 300000) {
  // 1. 먼저 메모리 캐시 확인
  if (promiseCache.has(cacheKey)) {
    return promiseCache.get(cacheKey);
  }

  // 2. 로컬 스토리지 캐시 확인 (사용하는 경우)
  if (useLocalStorage) {
    const lsKey = `ls_cache_${cacheKey}`;
    const cached = localStorage.getItem(lsKey);

    if (cached) {
      try {
        const { data, expiry } = JSON.parse(cached);
        // 만료 시간 확인
        if (expiry > Date.now()) {
          // 유효한 캐시가 있으면 Promise로 래핑하여 반환
          const cachedPromise = Promise.resolve(data);
          promiseCache.set(cacheKey, cachedPromise);
          return cachedPromise;
        } else {
          // 만료된 캐시는 삭제
          localStorage.removeItem(lsKey);
        }
      } catch (e) {
        // JSON 파싱 오류 시 캐시 삭제
        localStorage.removeItem(lsKey);
      }
    }
  }

  // 3. 캐시 없음, API 호출 실행
  const promise = apiCallFn().then(response => {
    // 로컬 스토리지에 저장 (사용하는 경우)
    if (useLocalStorage) {
      try {
        const lsKey = `ls_cache_${cacheKey}`;
        const cacheData = {
          data: response,
          expiry: Date.now() + localStorageTime
        };
        localStorage.setItem(lsKey, JSON.stringify(cacheData));
      } catch (e) {
        // 저장 실패 시 무시 (로컬 스토리지 크기 초과 등의 이유)
        console.warn('Failed to cache in localStorage:', e);
      }
    }
    return response;
  }).finally(() => {
    // 메모리 캐시 만료 처리
    setTimeout(() => {
      promiseCache.delete(cacheKey);
    }, memCacheTime);
  });

  // 메모리 캐시에 저장 후 반환
  promiseCache.set(cacheKey, promise);
  return promise;
}

/**
 * 모든 캐시를 클리어하는 함수
 */
function clearAllCache() {
  // 메모리 캐시 클리어
  promiseCache.clear();

  // 로컬 스토리지에서 캐시된 데이터 클리어
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('ls_cache_')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

export default {
  async LOGIN ({commit}, {email, pw, rcv}) {

    const login = {
      'login':email,
      'password':pw,
      'g-recaptcha-response': rcv
    }

    return new Promise((resolve, reject) => {
      HttpRequest.post('/api/auth/login', login, true)
        .then(response => {
          commit('LOGIN', response)
          resolve(response)
        }).catch(err => { reject(err) })
    })
  },

  async REFRESH_TOKEN ({commit}) {
    const option = {
      token: VueCookie.get('token')
    }

    return new Promise((resolve, reject) => {
      HttpRequest.post('/api/auth/refresh-token', option)
        .then(response => {
          commit('REFRESH_TOKEN', response)
          resolve(response)
        }).catch(err => { reject(err) })
    })
  },

  REGISTER_MEMBER ({commit}, {id, email, password, confirm}) {

    const register = {
      username:id,
      email,
      password,
      password_confirmation:confirm,
      // 신규 항목
      nickname:'',
      birthdate:'',
      mobile:'',
      phone:'',
      home_zip:'',
      home_address_1:'',
      home_address_2:'',
      company_zip:'',
      company_address_1:'',
      company_address_2:'',
      subscribe_email:1,
      subscribe_sms:1,
      mail_destination:''
    }

    return new Promise((resolve, reject) => {
      HttpRequest.post('/api/auth/register', register)
        .then(response => {
          commit('REGISTER_MEMBER', response)
          resolve(response)
        }).catch(err => {

          switch (err.response.status) {
          default:
            alert(constant.ALERT_MSG_DEFAULT_ERROR)
            break
          }

          reject(err)

        })

    })
  },

  async USER_INFO ({commit, state}) {
    const cacheKey = getCacheKey('USER_INFO', {});

    return cachedApiCall(cacheKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.get('/api/auth/me')
          .then(async response => {
            const data = response
            const companyId = data.user.company_id

            // 만약 state.companyInfo.id 값이 없거나 data.user.company_id 값과 다르다면 API를 호출한다
            if (!state.companyInfo.id || `${state.companyInfo.id}` !== `${data.user.company_id}`) {
              try {
                const companyResponse = await HttpRequest.get(`/api/v1/company/${companyId}`)
                // mutations 메소드 호출
                commit('COMPANY_INFO', companyResponse)
              } catch (error) {
                // company 정보 조회 실패해도 user 정보는 정상적으로 처리
                console.warn('Failed to load company info:', error);
              }
            }

            resolve(data)
          }).catch(err => {
            reject(err)
          })
      })
    }).then(data => {
      // 캐시된 데이터든 새로 불러온 데이터든 항상 commit 호출
      commit('USER_INFO', data)

      // 회사 정보도 함께 처리 (캐시된 경우에도 필요)
      if (data && data.user && data.user.company_id) {
        const companyId = data.user.company_id
        if (!state.companyInfo.id || `${state.companyInfo.id}` !== `${companyId}`) {
          try {
            HttpRequest.get(`/api/v1/company/${companyId}`).then(response => {
              commit('COMPANY_INFO', response)
            }).catch(error => {
              console.warn('Failed to load company info:', error);
            });
          } catch (error) {
            console.warn('Failed to load company info:', error);
          }
        }
      }

      return data
    });
  },

  SET_MY_INFO ({commit}) {
    const myInfo = JSON.parse(localStorage.getItem('myInfo'))
    commit('SET_MY_INFO', myInfo)
  },

  // 로그아웃
  async USER_LOGOUT ({commit}) {
    // 모든 캐시 클리어
    clearAllCache();

    return new Promise((resolve, reject) => {
      HttpRequest.post('/api/auth/logout')
        .then(response => {
          commit('USER_LOGOUT')
          resolve(response)
        }).catch(err => reject(err))
    })

  },

  async USER_FORGOT_PASSWORD ({commit}, params) {
    const option = {
      params: {
        email:params.email
      }
    }

    return new Promise((resolve, reject) => {
      HttpRequest.post('/api/auth/forgot-password', option.params)
        .then(response => {
          commit('USER_FORGOT_PASSWORD', response)
          resolve(response)
        }).catch(err => {
          switch (err.response.status) {
          case 404:
            err.response.data = '입력하신 이메일로 가입된 계정이 없습니다.'
            break
          case 500:
            err.response.data = '이메일 전송에 실패했습니다. 관리자에게 문의해주세요.'
            break
          default:
            err.response.data = '이메일 전송에 실패했습니다.'
            break
          }
          reject(err)
        })
    })
  },

  async USER_RESET_PASSWORD ({commit}, params) {
    const option = {
      params: {
        reset_password_code: params.code,
        password: params.password,
        password_confirmation: params.repassword
      }
    }

    return new Promise((resolve, reject) => {
      HttpRequest.post('/api/auth/reset-password', option.params)
        .then(response => {
          commit('USER_RESET_PASSWORD', response)
          resolve(response)
        }).catch(err => { reject(err) })
    })
  },

  async LOAD_CHECK_QR ({commit}, params) {
    const project_id = params.projectId
    const cacheKey = getCacheKey('LOAD_CHECK_QR', { project_id });

    return cachedApiCall(cacheKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.get(`/api/v1/project/qr/${project_id}/code`)
          .then(response => {
            resolve(response)
          }).catch(err => reject(err))
      })
    }).then(response => {
      // 캐시된 데이터든 새로 불러온 데이터든 항상 commit 호출
      commit('LOAD_CHECK_QR', response)
      return response
    });
  },

  async IS_QR_SCAN_MANAGER ({commit}, params) {
    const project_id = params.projectId
    const cacheKey = getCacheKey('IS_QR_SCAN_MANAGER', { project_id });

    return cachedApiCall(cacheKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.get(`/api/v1/project/qr/${project_id}`)
          .then(response => {
            resolve(response)
          }).catch(err => reject(err))
      })
    }).then(response => {
      // 캐시된 데이터든 새로 불러온 데이터든 항상 commit 호출
      commit('IS_QR_SCAN_MANAGER', response)
      return response
    });
  }

}
